import { Banner } from '@/components/ui/banner'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SkeletonRows } from '@/components/ui/skeleton'
import { useInternalEndorsement } from '@/models/internalEndorsement'
import { PolicyChangeStatus } from '@/models/policyChange'
import { AskHistory } from '../../PolicyChanges/AskHistory'
import { EndorsementBind } from './EndorsementBind'
import { EndorsementChangeLines } from './EndorsementChangeLines'
import {
  needsPortal,
  STAFF_NEXT_STEP,
  STAFF_NEXT_STEP_SUBMITTED_WITH_CORGI,
  STAFF_NEXT_STEP_WITH_CARRIER_WITH_CORGI,
} from './endorsementCopy'
import { EndorsementHeader } from './EndorsementHeader'
import { EndorsementQuotes } from './EndorsementQuotes'

/** One request as staff work it, from review to the new COI. */
export function EndorsementDetail({ requestId }: { requestId: string }) {
  const { data: endorsement, isLoading } = useInternalEndorsement(requestId)
  if (isLoading) return <SkeletonRows rows={6} />
  // Keyed on the data, not isError: a failed background poll keeps the loaded request, and with it the prices
  // staff are typing, on the page.
  if (!endorsement) {
    return <p className="text-destructive text-sm">Couldn&rsquo;t load this request. Refresh to try again.</p>
  }

  const hasCorgi = endorsement.policies.some((policy) => policy.corgiEligible)
  const corgiNextStep: Partial<Record<PolicyChangeStatus, string>> = {
    [PolicyChangeStatus.SUBMITTED]: STAFF_NEXT_STEP_SUBMITTED_WITH_CORGI,
    [PolicyChangeStatus.WITH_CARRIER]: STAFF_NEXT_STEP_WITH_CARRIER_WITH_CORGI,
  }
  const nextStep = (hasCorgi && corgiNextStep[endorsement.status]) || STAFF_NEXT_STEP[endorsement.status]
  const leftForPortal = endorsement.changes.filter(
    (line) => !line.dropped && (line.corgi ?? []).some(needsPortal)
  ).length

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {/* Keyed so a reason typed for one request never goes to the next. */}
      <EndorsementHeader key={endorsement.id} endorsement={endorsement} />
      {nextStep && <Banner variant="info">{nextStep}</Banner>}
      {leftForPortal > 0 && endorsement.status === PolicyChangeStatus.WITH_CARRIER && (
        <Banner variant="warning">
          Corgi did not take {leftForPortal === 1 ? 'one change' : `${leftForPortal} changes`}. Each says why
          below; add it in the Corgi portal before submitting the draft for a quote.
        </Banner>
      )}
      {endorsement.openAsk && (
        <Banner variant="warning">
          Waiting on the customer{endorsement.openAsk.needsDocument && ' for a document'}:{' '}
          {endorsement.openAsk.question}
        </Banner>
      )}
      {endorsement.status === PolicyChangeStatus.QUOTE_READY && (
        <Banner variant="warning">The quotes are with the customer to accept or decline.</Banner>
      )}
      {endorsement.status === PolicyChangeStatus.DECLINED && (
        <Banner variant="info">
          {endorsement.declineReason
            ? `Declined by Cai: ${endorsement.declineReason}`
            : 'The customer declined the quote. Nothing changed on the roster.'}
        </Banner>
      )}
      {endorsement.status === PolicyChangeStatus.CANCELED && (
        <Banner variant="info">
          The customer canceled it before it was quoted. Nothing changed on the roster.
        </Banner>
      )}
      <EndorsementChangeLines endorsement={endorsement} />
      {endorsement.asks.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Asked the customer</CardTitle>
          </CardHeader>
          <CardContent>
            <AskHistory asks={endorsement.asks} answerLabel="They answered" />
          </CardContent>
        </Card>
      )}
      {/* Another request, or a dropped change that cleared the prices, starts the quote form over. */}
      <EndorsementQuotes
        key={`${endorsement.id}-${endorsement.changes.filter((line) => line.dropped).length}`}
        endorsement={endorsement}
      />
      <EndorsementBind endorsement={endorsement} />
    </div>
  )
}
